import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getRegistrationForm, submitRegistration, fmtPublicDateTime,
} from '../api/publicRegistrationApi';
import '../styles/publicForms.css';

const SUBSCRIPTION_CHANNELS = [
  { key: 'email',    label: 'Email' },
  { key: 'push',     label: 'Push notifications' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

const EMPTY_SUBS = { email: false, push: false, whatsapp: false };

function EventSummary({ event }) {
  return (
    <div className="pf-event">
      <strong>When:</strong> {fmtPublicDateTime(event.start)}{event.end ? ` – ${fmtPublicDateTime(event.end)}` : ''}<br />
      {(event.location.name || event.location.address) && (
        <><strong>Where:</strong> {[event.location.name, event.location.address].filter(Boolean).join(', ')}</>
      )}
    </div>
  );
}

function Shell({ org, title, children }) {
  return (
    <div className="pf-page" style={{ '--pf-primary': org?.primaryColor || '#6B46C1' }}>
      <div className="pf-card">
        <div className="pf-head">
          {org?.logoUrl && <img src={org.logoUrl} alt="" />}
          <div className="pf-org">{org?.name}</div>
          <h1 className="pf-title">{title}</h1>
        </div>
        <div className="pf-body">{children}</div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, error, onChange }) {
  const id = `pf-${field.key}`;
  const common = { id, className: `pf-input${error ? ' pf-input--error' : ''}`, 'aria-invalid': !!error };

  switch (field.type) {
    case 'subscriptions': {
      const subs = value || EMPTY_SUBS;
      return (
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="pf-legend">{field.label}</legend>
          <div className="pf-hint" style={{ marginBottom: 6 }}>Tick the channels you'd like us to contact you on.</div>
          {SUBSCRIPTION_CHANNELS.map(c => (
            <label key={c.key} className="pf-check">
              <input type="checkbox" checked={!!subs[c.key]} onChange={e => onChange({ ...subs, [c.key]: e.target.checked })} />
              {c.label}
            </label>
          ))}
        </fieldset>
      );
    }
    case 'checkbox':
      return (
        <label className="pf-check">
          <input id={id} type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
          <span>{field.label}{field.required && <span className="pf-req">*</span>}</span>
        </label>
      );
    case 'select':
      return (
        <select {...common} value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'textarea':
      return <textarea {...common} rows={3} maxLength={2000} value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'nric4':
      return <input {...common} type="text" maxLength={4} autoComplete="off" placeholder="e.g. 567A" value={value || ''} onChange={e => onChange(e.target.value.toUpperCase())} />;
    case 'year':
      return <input {...common} type="text" inputMode="numeric" maxLength={4} placeholder="e.g. 1975" value={value || ''} onChange={e => onChange(e.target.value.replace(/\D/g, ''))} />;
    case 'email':
      return <input {...common} type="email" autoComplete="email" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'tel':
      return <input {...common} type="tel" autoComplete="tel" placeholder="e.g. 9123 4567" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'number':
      return <input {...common} type="number" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'date':
      return <input {...common} type="date" value={value || ''} onChange={e => onChange(e.target.value)} />;
    default:
      return <input {...common} type="text" maxLength={300} autoComplete={field.key === 'name' ? 'name' : 'off'} value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
}

export default function EventRegistrationPage() {
  const { orgId, eventId } = useParams();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [values, setValues] = useState({ subscriptions: EMPTY_SUBS });
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getRegistrationForm(orgId, eventId)
      .then(data => { if (!cancelled) setState({ loading: false, error: '', data }); })
      .catch(e => { if (!cancelled) setState({ loading: false, error: e.message, data: null }); });
    return () => { cancelled = true; };
  }, [orgId, eventId]);

  const setValue = (key, v) => {
    setValues(p => ({ ...p, [key]: v }));
    setErrors(p => (p[key] ? { ...p, [key]: undefined } : p));
  };

  const validateLocally = (fields) => {
    const errs = {};
    fields.forEach(f => {
      if (!f.required || f.type === 'subscriptions') return;
      const v = values[f.key];
      if (f.type === 'checkbox' ? !v : (v === undefined || v === null || String(v).trim() === '')) errs[f.key] = `${f.label} is required.`;
    });
    if (!consent) errs.consent = 'Please confirm your consent to continue.';
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const { fields } = state.data;
    const local = validateLocally(fields);
    setErrors(local);
    setFormError('');
    if (Object.keys(local).length) { setFormError('Please correct the highlighted fields.'); return; }

    setSubmitting(true);
    try {
      const res = await submitRegistration(orgId, eventId, { values, consent, website });
      setDone(res);
    } catch (err) {
      if (err.fieldErrors) setErrors(err.fieldErrors);
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const { loading, error, data } = state;
  if (loading) return <Shell title="Loading…"><div className="pf-center">Please wait…</div></Shell>;
  if (error || !data) return <Shell title="Event registration"><div className="pf-alert pf-alert--error">{error || 'This registration page is not available.'}</div></Shell>;

  const { org, event, fields } = data;

  if (done) {
    const waitlisted = done.status === 'WAITLISTED';
    return (
      <Shell org={org} title={waitlisted ? "You're on the waitlist" : "You're registered!"}>
        <div className="pf-center">
          <div className="pf-big" aria-hidden="true">{waitlisted ? '⏳' : '🎉'}</div>
          <p>
            {waitlisted
              ? `This event is full. You are number ${done.waitlist_position} on the waitlist and we'll be in touch if a place opens up.`
              : 'Thank you! Your place is confirmed.'}
          </p>
        </div>
        <h2 style={{ fontSize: 17, margin: '8px 0' }}>{done.event?.title || event.title}</h2>
        <EventSummary event={done.event || event} />
        <p className="pf-hint">If this event's date, time or location changes, we'll contact you on the channels you subscribed to. You can now close this page.</p>
      </Shell>
    );
  }

  if (!data.registrationOpen) {
    return (
      <Shell org={org} title={event.title}>
        <EventSummary event={event} />
        <div className="pf-alert pf-alert--info">{data.closedReason || 'Registration is not open for this event.'}</div>
      </Shell>
    );
  }

  return (
    <Shell org={org} title={event.title}>
      <EventSummary event={event} />
      {event.description && <p style={{ fontSize: 14, color: '#4B5563', whiteSpace: 'pre-line' }}>{event.description}</p>}
      {data.waitlistOnly && (
        <div className="pf-alert pf-alert--info">This event is currently full. You can still register and join the waitlist.</div>
      )}

      <form onSubmit={onSubmit} noValidate>
        {fields.map(f => (
          <div className="pf-field" key={f.key}>
            {f.type !== 'checkbox' && f.type !== 'subscriptions' && (
              <label htmlFor={`pf-${f.key}`}>{f.label}{f.required && <span className="pf-req">*</span>}</label>
            )}
            <FieldInput field={f} value={values[f.key]} error={errors[f.key]} onChange={v => setValue(f.key, v)} />
            {f.type === 'nric4' && <div className="pf-hint">Only the last 3 digits and the letter — never your full NRIC.</div>}
            {errors[f.key] && <div className="pf-error" role="alert">{errors[f.key]}</div>}
          </div>
        ))}

        <div className="pf-hp" aria-hidden="true">
          <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} /></label>
        </div>

        <div className="pf-field">
          <label className="pf-check pf-consent">
            <input type="checkbox" checked={consent} onChange={e => { setConsent(e.target.checked); setErrors(p => ({ ...p, consent: undefined })); }} />
            <span>
              I consent to {org.name} collecting and using my personal data to manage my registration for this event and to
              contact me about it, including on the channels I have subscribed to above.<span className="pf-req">*</span>
            </span>
          </label>
          {errors.consent && <div className="pf-error" role="alert">{errors.consent}</div>}
        </div>

        {formError && <div className="pf-alert pf-alert--error" role="alert">{formError}</div>}
        <button className="pf-btn" type="submit" disabled={submitting}>
          {submitting ? 'Submitting…' : data.waitlistOnly ? 'Join waitlist' : 'Register'}
        </button>
      </form>
    </Shell>
  );
}
